/**
 * CeremonyHalo — full-viewport edge glow that fires on trip state transitions.
 * See DESIGN_LANGUAGE_v2 §4.2 (Halo plane), §7 cues.
 *
 * Usage:
 *   <CeremonyHalo trigger={trip?.status} role="rider" />
 *   whenever `trigger` changes to a value in TRIGGER_STATES the halo fires once.
 */
import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ROLE_ACCENT, ROLE_ACCENT_GLOW, DUR, EASE, prefersReducedMotion } from "../../lib/motion";
import { cue } from "../../lib/soundCues";

// Which trip statuses fire a halo, and the accent tone override + cue to play.
const CEREMONY_MAP = {
  ARRIVED_PU:  { tone: "violet", cueName: "confirm",     label: "Pickup ceremony" },
  ON_TRIP:     { tone: "amber",  cueName: "streak_extend", label: "En route" },
  ARRIVED_DO:  { tone: "violet", cueName: "confirm",     label: "Arrived at destination" },
  COMPLETED:   { tone: "forest", cueName: "pod_signed",  label: "Drop-off ceremony · POD stitched" },
  ACCEPTED:    { tone: "emerald", cueName: "confirm",     label: "Driver confirmed" },
  EN_ROUTE:    { tone: "amber",   cueName: "streak_extend", label: "Driver en route" },
};

const TONE_COLOR = {
  violet: "rgba(139,92,246,0.55)",
  amber:  "rgba(245,158,11,0.60)",
  forest: "rgba(16,185,129,0.60)",
  emerald:"rgba(16,185,129,0.55)",
  sky:    "rgba(59,130,246,0.55)",
};

export default function CeremonyHalo({ trigger, role = "rider" }) {
  const [active, setActive] = useState(null); // {tone, label}
  const prev = useRef(trigger);
  const timer = useRef(null);

  useEffect(() => {
    if (prev.current !== trigger) {
      const spec = CEREMONY_MAP[trigger];
      prev.current = trigger;
      if (!spec) return;
      // Fire cue
      try { cue[spec.cueName] && cue[spec.cueName](); } catch { /* noop */ }
      // Show halo
      setActive({ tone: spec.tone, label: spec.label });
      clearTimeout(timer.current);
      timer.current = setTimeout(() => setActive(null), 1400);
    }
  }, [trigger]);

  useEffect(() => () => clearTimeout(timer.current), []);
  if (prefersReducedMotion()) return null;

  return (
    <AnimatePresence>
      {active && (
        <motion.div
          data-testid="ceremony-halo"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: DUR.smooth, ease: EASE.emerge }}
          style={{
            position: "fixed", inset: 0, pointerEvents: "none", zIndex: 9998,
            boxShadow: `inset 0 0 120px 20px ${TONE_COLOR[active.tone] || ROLE_ACCENT_GLOW[role]}`,
          }}
        >
          <motion.div
            data-testid="ceremony-halo-label"
            initial={{ y: -30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -20, opacity: 0 }}
            transition={{ duration: DUR.reveal, ease: EASE.pop }}
            style={{
              position: "absolute", top: 24, left: "50%", transform: "translateX(-50%)",
              padding: "8px 16px", borderRadius: 999,
              background: "rgba(11,18,32,0.92)", border: `1px solid ${TONE_COLOR[active.tone]}`,
              backdropFilter: "blur(12px)",
              fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase",
              color: "#E2E8F0", fontWeight: 800,
              boxShadow: `0 8px 32px ${TONE_COLOR[active.tone]}`,
            }}
          >
            {active.label}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
